// src/pages/VieClub/VieClubPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import VieClubCard from "./VieClubCard";
import { useUser } from "../../contexts/UserContext";
import useScrollToTop from "../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import { getAllArticles } from "../../services/vieclubServices";
import CreateArticleModal from "./CreateArticleModal";
import EditArticleModal from "./EditArticleModal";
import Button from "../../components/Button/Button";
import { FaPlus, FaFilter, FaRedo, FaSortAmountDownAlt } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

const VieClubPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = user?.role_id ?? 0; // 0 = visiteur

  const [articles, setArticles] = useState([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentArticle, setCurrentArticle] = useState(null);

  // filtres
  const [searchText, setSearchText] = useState("");
  const [selectedClub, setSelectedClub] = useState("");
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [sortMode, setSortMode] = useState("dateDesc"); // dateDesc | dateAsc

  const [clubsList, setClubsList] = useState([]);

  const canCreate = [1, 2, 5].includes(userRole);
  const canSeeHidden = [1, 2, 5, 7].includes(userRole);

  useEffect(() => {
    const fetchArticles = async () => {
      const fetchedArticles = await getAllArticles();
      setArticles(fetchedArticles || []);

      const clubs = [
        ...new Set(
          (fetchedArticles || [])
            .map((a) => a.club)
            .filter((c) => !!c && c.trim() !== "")
        ),
      ].sort((a, b) => a.localeCompare(b));
      setClubsList(clubs);
    };

    fetchArticles();
  }, []);

  const refreshArticlesList = async () => {
    const fetchedArticles = await getAllArticles();
    setArticles(fetchedArticles || []);
  };

  const openCreateModal = () => setCreateModalOpen(true);
  const closeCreateModal = () => setCreateModalOpen(false);

  const handleOpenEditModal = (article) => {
    setCurrentArticle(article);
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setCurrentArticle(null);
  };

  const handleResetFilters = () => {
    setSearchText("");
    setSelectedClub("");
    setSortMode("dateDesc");
    setShowOnlyActive(true);
  };

  const filteredArticles = useMemo(() => {
    let list = [...articles];

    if (showOnlyActive || !canSeeHidden) {
      list = list.filter((a) => a.is_active);
    }

    if (selectedClub) {
      list = list.filter((a) => a.club === selectedClub);
    }

    if (searchText) {
      const q = searchText.toLowerCase();
      list = list.filter(
        (a) =>
          (a.title || "").toLowerCase().includes(q) ||
          (a.content || "").toLowerCase().includes(q) ||
          (a.club || "").toLowerCase().includes(q)
      );
    }

    list.sort((a, b) => {
      const da = new Date(a.date_article);
      const db = new Date(b.date_article);
      return sortMode === "dateAsc" ? da - db : db - da;
    });

    return list;
  }, [
    articles,
    searchText,
    selectedClub,
    showOnlyActive,
    sortMode,
    canSeeHidden,
  ]);

  const totalCount = articles.length;
  const visibleCount = filteredArticles.length;

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Utilisateurs & Visiteurs"
        title="La Vie des Clubs"
        subtitle="Partagez les moments forts des clubs girondins : résultats sportifs, événements marquants, stages, animations…"
        align="center"
        size="lg"
        className="mb-6"
      />

      <InfoBubble title="Bienvenue sur la page La Vie des Clubs">
        <p>
          Les responsables de clubs peuvent publier ici des articles illustrés
          pour mettre en avant la vie de leur structure. Les articles peuvent
          contenir du texte, des photos et un lien externe (site, réseau social,
          article de presse…).
        </p>
      </InfoBubble>

      {/* Panneau filtres */}
      <div className="max-w-[1280px] w-full mx-auto mt-6 mb-6">
        <div className="rounded-2xl bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-700/80 shadow-lg px-4 sm:px-6 py-4 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-orange-100 text-orange-500">
                <FaFilter />
              </span>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
                  Filtres & recherche
                </p>
                <p className="text-[11px] text-slate-400 dark:text-slate-400">
                  Affichez uniquement les articles qui vous intéressent.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <FaSortAmountDownAlt className="text-slate-400" />
              <select
                value={sortMode}
                onChange={(e) => setSortMode(e.target.value)}
                className="select select-bordered select-xs sm:select-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              >
                <option value="dateDesc">Du plus récent au plus ancien</option>
                <option value="dateAsc">Du plus ancien au plus récent</option>
              </select>

              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1 rounded-full border border-slate-200/80 dark:border-slate-700/80 px-3 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <FaRedo className="text-[10px]" />
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="col-span-1 md:col-span-1">
              <select
                value={selectedClub}
                onChange={(e) => setSelectedClub(e.target.value)}
                className="select select-bordered w-full dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              >
                <option value="">Tous les clubs</option>
                {clubsList.map((club) => (
                  <option key={club} value={club}>
                    {club}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-1 md:col-span-1">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Rechercher par titre, club ou contenu…"
                className="input input-bordered w-full text-sm dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              />
            </div>

            {canSeeHidden && (
              <div className="col-span-1 flex items-center justify-start md:justify-end">
                <label className="inline-flex items-center gap-2 cursor-pointer text-xs sm:text-sm text-slate-600 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={showOnlyActive}
                    onChange={(e) => setShowOnlyActive(e.target.checked)}
                    className="toggle toggle-sm toggle-primary"
                  />
                  <span>Afficher uniquement les articles publiés</span>
                </label>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 text-[11px] mt-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-1 text-slate-600 dark:text-slate-200">
              <span className="font-semibold">Total articles :</span>{" "}
              {totalCount}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 dark:bg-orange-900/30 px-3 py-1 text-orange-700 dark:text-orange-300">
              <span className="font-semibold">Article(s) affiché(s) :</span>{" "}
              {visibleCount}
            </span>
          </div>
        </div>
      </div>

      {/* Bouton création */}
      {canCreate && (
        <div className="flex justify-center mb-4">
          <Button
            text="Ajouter un Article"
            onClick={openCreateModal}
            icon={FaPlus}
            className="btn-primary gap-2"
          />
        </div>
      )}

      {/* Liste articles */}
      <div className="mx-auto max-w-[1280px] w-full p-4 space-y-6">
        {filteredArticles.map((article, index) => (
          <VieClubCard
            key={article.id}
            article={article}
            index={index}
            userRole={userRole}
            refreshArticlesList={refreshArticlesList}
            user={user}
            onEdit={() => handleOpenEditModal(article)}
          />
        ))}
        {filteredArticles.length === 0 && (
          <p className="text-center text-sm text-slate-500 dark:text-slate-300 mt-6">
            Aucun article ne correspond aux critères sélectionnés.
          </p>
        )}
      </div>

      {createModalOpen && (
        <CreateArticleModal
          isOpen={createModalOpen}
          onClose={closeCreateModal}
          refreshList={refreshArticlesList}
        />
      )}

      {editModalOpen && currentArticle && (
        <EditArticleModal
          isOpen={editModalOpen}
          onClose={handleCloseEditModal}
          refreshList={refreshArticlesList}
          article={currentArticle}
        />
      )}
    </div>
  );
};

export default VieClubPage;
